import { useCallback, useEffect, useState } from 'react';
import {
  getNotificationModules,
  setNotificationMaster,
  setNotificationModuleState,
} from '../api/General/NotificationModules';
import type { NotificationModulesView, NotificationModuleView } from '../api/General/NotificationModules';

/**
 * CO-375 / E13 — the company's notification module answers, fetched ONCE per session and shared by the
 * first-visit prompt (MasterLayout) and the Modules tab on notification management, so answering in one
 * place updates the other without a refetch.
 *
 * `enabled` false (e.g. a non-HR role on the prompt) makes no request at all: the endpoint is HR/Admin only
 * and a Clocking user must not be sent into a 403 on every page.
 *
 * `fresh` true (review-3 finding 3) bypasses the session cache on mount and reports `loading` until the
 * server answers. The Modules tab is the authority screen for the master switch and the test-company mark,
 * both of which another person (Admin in another browser, an Operator marking the company) can change, so it
 * must never render the snapshot the first page's prompt cached.
 */
let cached: NotificationModulesView | null = null;
let inflight: Promise<NotificationModulesView | null> | null = null;
const listeners = new Set<(v: NotificationModulesView | null) => void>();

function publish(next: NotificationModulesView | null) {
  cached = next;
  listeners.forEach((l) => l(next));
}

async function load(force = false): Promise<NotificationModulesView | null> {
  if (cached && !force) return cached;
  if (inflight && !force) return inflight;
  const request = getNotificationModules()
    .then((res) => {
      const view = !res.errors?.length && res.payload ? res.payload : null;
      publish(view);
      return view;
    })
    .catch(() => null)
    .finally(() => {
      if (inflight === request) inflight = null;
    });
  inflight = request;
  return request;
}

/** Drop the session cache (sign-out / company switch). */
export function resetNotificationModulesCache() {
  cached = null;
  inflight = null;
}

export function useNotificationModules(enabled: boolean, options?: { fresh?: boolean }) {
  const fresh = options?.fresh ?? false;
  // A fresh reader never shows the cached snapshot: it starts empty and loading until the refetch lands.
  const [data, setData] = useState<NotificationModulesView | null>(fresh ? null : cached);
  const [loading, setLoading] = useState(enabled && (fresh || !cached));
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    listeners.add(setData);
    let alive = true;
    load(fresh).then((v) => {
      if (!alive) return;
      setData(v);
      setFailed(v == null);
      setLoading(false);
    });
    return () => {
      alive = false;
      listeners.delete(setData);
    };
  }, [enabled, fresh]);

  const reload = useCallback(async () => {
    setLoading(true);
    const v = await load(true);
    setFailed(v == null);
    setLoading(false);
  }, []);

  /** Records an answer; returns an error message or null. Updates every subscriber on success. */
  const setModuleState = useCallback(
    async (key: string, state: 'On' | 'Off', source: 'prompt' | 'settings'): Promise<string | null> => {
      const res = await setNotificationModuleState(key, state, source);
      if (res.errors?.length || !res.payload) return res.errors?.[0]?.description ?? 'error';
      const updated: NotificationModuleView = res.payload;
      if (cached) {
        publish({
          ...cached,
          modules: cached.modules.map((m) => (m.key === key ? updated : m)),
        });
      }
      return null;
    },
    []
  );

  const setMasterDisabled = useCallback(async (disabled: boolean): Promise<string | null> => {
    const res = await setNotificationMaster(disabled);
    if (res.errors?.length) return res.errors[0]?.description ?? 'error';
    if (cached) publish({ ...cached, masterDisabled: disabled });
    // Re-read so the test-company mark and module answers come from the server, not from a spread of a
    // snapshot that may predate another actor's change (review-3 finding 3). The optimistic publish above
    // keeps the switch responsive meanwhile; a failed re-read keeps it (never blanks the tab).
    void getNotificationModules()
      .then((fresh) => {
        if (!fresh.errors?.length && fresh.payload) publish(fresh.payload);
      })
      .catch(() => undefined);
    return null;
  }, []);

  return { data, loading, failed, reload, setModuleState, setMasterDisabled };
}
